import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Button } from "../../Component";
import UserPicker from "../../partials/user-manage/UserPicker";
import swal from "sweetalert";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import { linkPrimaryUserToPartner } from "../../../utils/medicalPartnerPrimaryUser";
import { filterUserAssignableAsPrimaryPartner } from "./medicalPartnerListTableHelpers";

const MedicalPartnerAssignPrimaryUserModal = ({ isOpen, partner, onClose, onComplete }) => {
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelected(null);
      return;
    }
    setSelected(null);
  }, [isOpen, partner?._id]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const save = async () => {
    if (!partner?._id) return;
    if (!selected?._id) {
      swal("Validation", "Search and select a user from the directory.", "warning");
      return;
    }
    const uid = String(selected._id);
    const prevRaw = partner.userId ? partner.userId._id || partner.userId : "";
    const prev = prevRaw ? String(prevRaw) : "";
    if (prev && prev === uid) {
      swal("Info", "This user is already the primary account.", "info");
      return;
    }
    if (prev && prev !== uid) {
      const ok = await swal({
        title: "Change primary account?",
        text: "The previous primary user may lose partner ownership on their profile unless they remain a co-manager.",
        icon: "warning",
        buttons: ["Cancel", "Change primary"],
        dangerMode: true,
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      await linkPrimaryUserToPartner(partner._id, uid);
      if (typeof onComplete === "function") await onComplete();
      swal("Success", prev ? "Primary account updated." : "Primary account linked.", "success");
      onClose();
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Update failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!partner) return null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} className="modal-dialog-centered" backdrop={saving ? "static" : true}>
      <ModalHeader toggle={handleClose}>Assign primary user</ModalHeader>
      <ModalBody>
        <p className="text-soft small mb-3">
          The primary account is the human identity for this partner. Only users not already linked as primary to another
          partner are listed. Pick an existing directory user — contact name and email follow
          this account.
        </p>
        <UserPicker
          idPrefix={`assign-primary-${partner._id || "partner"}`}
          value={selected}
          onChange={setSelected}
          disabled={saving}
          filterCandidate={(u) => filterUserAssignableAsPrimaryPartner(u, partner)}
          placeholder="Search by email or name…"
          emptyHint="No eligible users found."
        />
      </ModalBody>
      <ModalFooter>
        <Button color="light" type="button" disabled={saving} onClick={handleClose}>
          Cancel
        </Button>
        <Button color="primary" type="button" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save assignment"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MedicalPartnerAssignPrimaryUserModal;
